import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useDocument } from '../hooks/useFirestore';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

interface School {
  id: string;
  schoolName: string;
  email: string;
  phoneNumber: string;
  schoolAddress: string;
  stripeConnectAccountId?: string;
  creditCardRevenue: number;
  isActive: boolean;
  createdAt: Date;
}

export default function SchoolDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: school, loading } = useDocument<School>('users', id || '');
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');

  const handleSuspend = async () => {
    if (!id || !suspendReason.trim()) return;

    try {
      await updateDoc(doc(db, 'users', id), {
        isActive: false,
        suspendReason: suspendReason.trim(),
        suspendedAt: new Date(),
      });
      setShowSuspendDialog(false);
    } catch (error) {
      console.error('Failed to suspend school:', error);
    }
  };

  if (loading || !school) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">
          {school.schoolName}
        </Typography>
        {school.isActive ? (
          <Button
            variant="contained"
            color="error"
            onClick={() => setShowSuspendDialog(true)}
          >
            アカウント停止
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              await updateDoc(doc(db, 'users', id!), {
                isActive: true,
                suspendReason: null,
                suspendedAt: null,
              });
            }}
          >
            アカウント復活
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                基本情報
              </Typography>
              <Typography>
                メールアドレス: {school.email || '-'}
              </Typography>
              <Typography>
                電話番号: {school.phoneNumber || '-'}
              </Typography>
              <Typography>
                住所: {school.schoolAddress || '-'}
              </Typography>
              <Typography>
                登録日: {(() => { const v: any = (school as any).createdAt; try { const d = typeof v?.toDate === 'function' ? v.toDate() : new Date(v); return d.toLocaleDateString(); } catch { return ''; } })()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                決済情報
              </Typography>
              <Typography>
                Stripe Connect: {school.stripeConnectAccountId ? '連携済み' : '未連携'}
              </Typography>
              <Typography>
                クレジット決済収益: ¥{Number((school as any).creditCardRevenue ?? 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={showSuspendDialog}
        onClose={() => setShowSuspendDialog(false)}
      >
        <DialogTitle>アカウント停止</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="停止理由"
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSuspendDialog(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleSuspend}
            color="error"
            disabled={!suspendReason.trim()}
          >
            停止する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}